import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  getDocs,
  getDoc,
  deleteDoc,
  query,
  orderBy,
  writeBatch,
} from "firebase/firestore";

import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

import { dbLOA as db, storage, auth } from "../../../../backend/firebaseConfig";
import { PROVIDER_NETWORK_PATH } from "./providersSchema";
import { useUserEmail } from "../../../SignIn/UserEmailContext";

const normaliseEmail = (email = "") => email.trim().toLowerCase();

const favouriteProvidersCollectionRef = (userEmail) =>
  collection(
    db,
    "ProviderNetworkFavourites",
    normaliseEmail(userEmail),
    "Providers",
  );

const favouriteProviderRef = (userEmail, providerId) =>
  doc(
    db,
    "ProviderNetworkFavourites",
    normaliseEmail(userEmail),
    "Providers",
    providerId,
  );

const safeFileName = (name = "") =>
  String(name)
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .toLowerCase();

const providersCollectionRef = () => collection(db, ...PROVIDER_NETWORK_PATH);

const providerDocRef = (providerId) =>
  doc(db, ...PROVIDER_NETWORK_PATH, providerId);

const plansCollectionRef = (providerId) =>
  collection(db, ...PROVIDER_NETWORK_PATH, providerId, "Plans");

const planDocRef = (providerId, planId) =>
  doc(db, ...PROVIDER_NETWORK_PATH, providerId, "Plans", planId);

const PROVIDER_NETWORK_LOGS_COLLECTION = "ProviderNetworkLogs";

const cleanValueForLog = (value) => {
  if (value instanceof File) return value.name;

  if (Array.isArray(value)) {
    return value.map(cleanValueForLog);
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, item]) => {
      if (key === "attachmentFiles") return acc;

      acc[key] = cleanValueForLog(item);
      return acc;
    }, {});
  }

  return value ?? "";
};

const normaliseForCompare = (value) => {
  if (value === undefined || value === null) return "";

  if (Array.isArray(value)) {
    return value
      .map((item) => normaliseForCompare(item))
      .filter((item) => item !== "");
  }

  if (typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        if (key === "attachmentFiles") return acc;
        acc[key] = normaliseForCompare(value[key]);
        return acc;
      }, {});
  }

  return String(value).trim();
};

const valuesAreDifferent = (oldValue, newValue) => {
  return (
    JSON.stringify(normaliseForCompare(oldValue)) !==
    JSON.stringify(normaliseForCompare(newValue))
  );
};

const formatProviderPlanForCompare = (plan = {}) => ({
  planName: plan.planName || "",
  planType: plan.planType || "",
  policyStartingWith: plan.policyStartingWith || "",
  addresses: plan.addresses || [],
  emails: plan.emails || [],
  contacts: plan.contacts || [],
  websites: plan.websites || [],
  loaGuidelines: {
    acceptsScannedDocs: plan.loaGuidelines?.acceptsScannedDocs ?? null,
    acceptsLoaByPostOnly: plan.loaGuidelines?.acceptsLoaByPostOnly ?? null,
    acceptsDigitallySignedDocs:
      plan.loaGuidelines?.acceptsDigitallySignedDocs ?? null,
    acceptsWetSignatureOnly:
      plan.loaGuidelines?.acceptsWetSignatureOnly ?? null,
    acceptsLoaInsteadOfAuthority:
      plan.loaGuidelines?.acceptsLoaInsteadOfAuthority ?? null,
    secureEmail: plan.loaGuidelines?.secureEmail ?? null,
  },
  notes: plan.notes || "",
});

const PLAN_FIELD_LABELS = {
  planName: "Plan/Policy Name",
  planType: "Plan Type",
  policyStartingWith: "Policy Starting With",
  addresses: "Address",
  emails: "Email",
  contacts: "Contact",
  websites: "Website / Online Portal",
  notes: "Additional Notes",
  "loaGuidelines.acceptsScannedDocs": "Accepts scanned docs",
  "loaGuidelines.acceptsLoaByPostOnly": "Accepts LOAs by post only",
  "loaGuidelines.acceptsDigitallySignedDocs": "Accepts digitally signed docs",
  "loaGuidelines.acceptsWetSignatureOnly": "Accepts wet signature only",
  "loaGuidelines.acceptsLoaInsteadOfAuthority":
    "Accepts LOAs instead of authority",
  "loaGuidelines.secureEmail": "Secure Email",
};

const getNestedValue = (object, path) => {
  return path.split(".").reduce((current, key) => current?.[key], object);
};

const buildPlanFieldChanges = (oldPlan = {}, newPlan = {}) => {
  const oldCleanPlan = formatProviderPlanForCompare(oldPlan);
  const newCleanPlan = formatProviderPlanForCompare(newPlan);

  return Object.entries(PLAN_FIELD_LABELS)
    .map(([fieldPath, label]) => {
      const oldValue = getNestedValue(oldCleanPlan, fieldPath);
      const newValue = getNestedValue(newCleanPlan, fieldPath);

      if (!valuesAreDifferent(oldValue, newValue)) return null;

      return {
        field: label,
        oldValue: cleanValueForLog(oldValue),
        newValue: cleanValueForLog(newValue),
      };
    })
    .filter(Boolean);
};

const buildProviderUpdateChanges = ({
  previousProvider,
  providerName,
  logoData,
  validPlans,
}) => {
  const changes = [];

  if (valuesAreDifferent(previousProvider.providerName, providerName)) {
    changes.push({
      field: "Provider Name",
      oldValue: previousProvider.providerName || "",
      newValue: providerName,
    });
  }

  const previousLogo = previousProvider.providerLogoUrl
    ? "Logo available"
    : "No logo";
  const newLogo = logoData.providerLogoUrl ? "Logo available" : "No logo";

  if (previousLogo !== newLogo) {
    changes.push({
      field: "Provider Logo",
      oldValue: previousLogo,
      newValue: newLogo,
    });
  }

  const previousPlansById = new Map(
    (previousProvider.plans || []).map((plan) => [plan.id, plan]),
  );

  validPlans.forEach((newPlan, index) => {
    const previousPlan = previousPlansById.get(newPlan.id);

    if (!previousPlan) {
      changes.push({
        field: `Plan added: ${newPlan.planName || `Plan ${index + 1}`}`,
        oldValue: "-",
        newValue: cleanValueForLog(formatProviderPlanForCompare(newPlan)),
      });
      return;
    }

    const planChanges = buildPlanFieldChanges(previousPlan, newPlan);

    planChanges.forEach((change) => {
      changes.push({
        field: `${newPlan.planName || previousPlan.planName || `Plan ${index + 1}`} - ${change.field}`,
        oldValue: change.oldValue,
        newValue: change.newValue,
      });
    });
  });

  const newPlanIds = new Set(validPlans.map((plan) => plan.id).filter(Boolean));

  (previousProvider.plans || []).forEach((oldPlan) => {
    if (!newPlanIds.has(oldPlan.id)) {
      changes.push({
        field: `Plan removed: ${oldPlan.planName || "Unnamed Plan"}`,
        oldValue: cleanValueForLog(formatProviderPlanForCompare(oldPlan)),
        newValue: "-",
      });
    }
  });

  return changes;
};

export const createProviderNetworkLog = async ({
  providerId,
  providerName,
  action,
  performedBy = "",
  summary,
  changes = [],
}) => {
  const payload = {
    providerId,
    providerName: providerName || "",
    action,
    performedBy: performedBy || localStorage.getItem("userEmail") || "",
    summary,
    changes,
    createdAt: new Date().toISOString(),
  };

  await addDoc(collection(db, PROVIDER_NETWORK_LOGS_COLLECTION), payload);

  return payload;
};

export const getProviderNetworkLogs = async (providerId) => {
  const logsQuery = query(
    collection(db, PROVIDER_NETWORK_LOGS_COLLECTION),
    orderBy("createdAt", "desc"),
  );

  const snap = await getDocs(logsQuery);

  return snap.docs
    .map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }))
    .filter((log) => log.providerId === providerId);
};

const uploadProviderLogo = async ({ providerId, file }) => {
  if (!file) {
    return {
      providerLogoUrl: "",
      providerLogoPath: "",
    };
  }

  const fileName = safeFileName(file.name);
  const filePath = `provider-network/${providerId}/logo/${Date.now()}-${fileName}`;
  const storageRef = ref(storage, filePath);

  await uploadBytes(storageRef, file);

  const url = await getDownloadURL(storageRef);

  return {
    providerLogoUrl: url,
    providerLogoPath: filePath,
  };
};

const uploadPlanAttachments = async ({ providerId, planId, files = [] }) => {
  if (!files.length) return [];

  const uploadedFiles = await Promise.all(
    files.map(async (file) => {
      const fileName = safeFileName(file.name);
      const filePath = `provider-network/${providerId}/plans/${planId}/attachments/${Date.now()}-${fileName}`;
      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, file);

      const url = await getDownloadURL(storageRef);

      return {
        name: file.name,
        url,
        path: filePath,
        type: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
      };
    }),
  );

  return uploadedFiles;
};

export const getProviders = async () => {
  const q = query(
    providersCollectionRef(),
    orderBy("providerNameLower", "asc"),
  );
  const snap = await getDocs(q);

  const providers = await Promise.all(
    snap.docs.map(async (docSnap) => {
      const providerData = {
        id: docSnap.id,
        ...docSnap.data(),
      };

      try {
        const plansQ = query(
          plansCollectionRef(docSnap.id),
          orderBy("planNameLower", "asc"),
        );
        const plansSnap = await getDocs(plansQ);
        providerData.plans = plansSnap.docs.map((planSnap) => ({
          id: planSnap.id,
          ...planSnap.data(),
        }));
      } catch (err) {
        providerData.plans = [];
      }

      return providerData;
    }),
  );

  return providers;
};

export const getProviderById = async (providerId) => {
  const providerSnap = await getDoc(providerDocRef(providerId));

  if (!providerSnap.exists()) {
    throw new Error("Provider not found.");
  }

  const plansQ = query(
    plansCollectionRef(providerId),
    orderBy("planNameLower", "asc"),
  );
  const plansSnap = await getDocs(plansQ);

  return {
    id: providerSnap.id,
    ...providerSnap.data(),
    plans: plansSnap.docs.map((planSnap) => ({
      id: planSnap.id,
      ...planSnap.data(),
    })),
  };
};

export const createProviderWithPlans = async ({
  provider,
  providerLogoFile = null,
  plans = [],
  performedBy = "",
}) => {
  const providerName = provider.providerName?.trim();

  if (!providerName) {
    throw new Error("Provider name is required.");
  }

  if (!plans.length) {
    throw new Error("At least one plan/policy is required.");
  }

  const providerRef = await addDoc(providersCollectionRef(), {
    providerName,
    providerNameLower: providerName.toLowerCase(),
    providerLogoUrl: "",
    providerLogoPath: "",
    planCount: plans.length,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  const logoData = await uploadProviderLogo({
    providerId: providerRef.id,
    file: providerLogoFile,
  });

  await updateDoc(providerRef, {
    ...logoData,
    updatedAt: new Date().toISOString(),
  });

  const createdPlans = [];

  for (const plan of plans) {
    const planName = plan.planName?.trim();

    if (!planName) continue;

    const newPlanRef = doc(plansCollectionRef(providerRef.id));

    const attachments = await uploadPlanAttachments({
      providerId: providerRef.id,
      planId: newPlanRef.id,
      files: plan.attachmentFiles || [],
    });

    const cleanPlan = {
      planName,
      planNameLower: planName.toLowerCase(),
      planType: plan.planType || "",
      policyStartingWith: plan.policyStartingWith || "",

      addresses: (plan.addresses || []).filter(Boolean),
      emails: (plan.emails || []).filter(Boolean),
      contacts: (plan.contacts || []).filter((contact) =>
        contact?.number?.trim(),
      ),
      websites: (plan.websites || []).filter(Boolean),

      loaGuidelines: {
        acceptsScannedDocs: plan.loaGuidelines?.acceptsScannedDocs ?? null,
        acceptsLoaByPostOnly: plan.loaGuidelines?.acceptsLoaByPostOnly ?? null,
        acceptsDigitallySignedDocs:
          plan.loaGuidelines?.acceptsDigitallySignedDocs ?? null,
        acceptsWetSignatureOnly:
          plan.loaGuidelines?.acceptsWetSignatureOnly ?? null,
        acceptsLoaInsteadOfAuthority:
          plan.loaGuidelines?.acceptsLoaInsteadOfAuthority ?? null,
        secureEmail: plan.loaGuidelines?.secureEmail ?? null,
      },

      notes: plan.notes || "",
      attachments,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await setDoc(newPlanRef, cleanPlan);

    createdPlans.push({
      id: newPlanRef.id,
      ...cleanPlan,
    });
  }

 await updateDoc(providerRef, {
   planCount: createdPlans.length,
   updatedAt: new Date().toISOString(),
 });

 await createProviderNetworkLog({
   providerId: providerRef.id,
   providerName,
   action: "created",
   performedBy,
   summary: `Created provider ${providerName}`,
   changes: [
     {
       field: "Provider",
       newValue: cleanValueForLog({
         providerName,
         planCount: createdPlans.length,
         plans: createdPlans,
       }),
     },
   ],
 });

 return {
   providerId: providerRef.id,
   plans: createdPlans,
 };
};

export const addPlanToProvider = async ({ providerId, plan }) => {
  const newPlanRef = doc(plansCollectionRef(providerId));

  const attachments = await uploadPlanAttachments({
    providerId,
    planId: newPlanRef.id,
    files: plan.attachmentFiles || [],
  });

  const planName = plan.planName?.trim();

  if (!planName) {
    throw new Error("Plan/Policy name is required.");
  }

await setDoc(newPlanRef, {
  planName,
  planNameLower: planName.toLowerCase(),
  planType: plan.planType || "",
  policyStartingWith: plan.policyStartingWith || "",

  addresses: (plan.addresses || []).filter(Boolean),
  emails: (plan.emails || []).filter(Boolean),
  contacts: (plan.contacts || []).filter((contact) => contact?.number?.trim()),
  websites: (plan.websites || []).filter(Boolean),

  loaGuidelines: {
    acceptsScannedDocs: plan.loaGuidelines?.acceptsScannedDocs ?? null,
    acceptsLoaByPostOnly: plan.loaGuidelines?.acceptsLoaByPostOnly ?? null,
    acceptsDigitallySignedDocs:
      plan.loaGuidelines?.acceptsDigitallySignedDocs ?? null,
    acceptsWetSignatureOnly:
      plan.loaGuidelines?.acceptsWetSignatureOnly ?? null,
    acceptsLoaInsteadOfAuthority:
      plan.loaGuidelines?.acceptsLoaInsteadOfAuthority ?? null,
    secureEmail: plan.loaGuidelines?.secureEmail ?? null,
  },

  notes: plan.notes || "",
  attachments,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

  const plansSnap = await getDocs(plansCollectionRef(providerId));

  await updateDoc(providerDocRef(providerId), {
    planCount: plansSnap.size,
    updatedAt: new Date().toISOString(),
  });

  return newPlanRef.id;
};

// export const toggleProviderFavourite = async (providerId, currentValue) => {
//   await updateDoc(providerDocRef(providerId), {
//     isFavourite: !currentValue,
//     updatedAt: new Date().toISOString(),
//   });
// };

export const getUserFavouriteProviderIds = async (userEmail) => {
  const finalUserEmail =
    auth.currentUser?.email || localStorage.getItem("userEmail") || userEmail;

  if (!finalUserEmail) return [];

  const snap = await getDocs(
    collection(
      db,
      "ProviderNetworkFavourites",
      normaliseEmail(finalUserEmail),
      "Providers",
    ),
  );

  return snap.docs.map((docSnap) => docSnap.id);
};

export const toggleProviderFavourite = async (
  providerId,
  isCurrentlyFavourite,
  userEmail,
) => {
  const finalUserEmail =
    auth.currentUser?.email || localStorage.getItem("userEmail") || userEmail;

  if (!finalUserEmail) {
    throw new Error("You must be signed in to bookmark a provider.");
  }

  const ref = doc(
    db,
    "ProviderNetworkFavourites",
    normaliseEmail(finalUserEmail),
    "Providers",
    providerId,
  );

  if (isCurrentlyFavourite) {
    await deleteDoc(ref);
    return;
  }

  await setDoc(ref, {
    providerId,
    createdAt: new Date().toISOString(),
  });
};

export const deleteProvider = async (providerId) => {
  const provider = await getProviderById(providerId);

  const batch = writeBatch(db);

  provider.plans.forEach((plan) => {
    batch.delete(planDocRef(providerId, plan.id));
  });

  batch.delete(providerDocRef(providerId));

  await batch.commit();

  if (provider.providerLogoPath) {
    try {
      await deleteObject(ref(storage, provider.providerLogoPath));
    } catch (err) {
      console.warn("Could not delete provider logo:", err);
    }
  }

  for (const plan of provider.plans) {
    for (const attachment of plan.attachments || []) {
      if (attachment.path) {
        try {
          await deleteObject(ref(storage, attachment.path));
        } catch (err) {
          console.warn("Could not delete attachment:", err);
        }
      }
    }
  }
};

export const deletePlan = async (providerId, planId) => {
  const planSnap = await getDoc(planDocRef(providerId, planId));

  if (!planSnap.exists()) return;

  const plan = planSnap.data();

  await deleteDoc(planDocRef(providerId, planId));

  for (const attachment of plan.attachments || []) {
    if (attachment.path) {
      try {
        await deleteObject(ref(storage, attachment.path));
      } catch (err) {
        console.warn("Could not delete attachment:", err);
      }
    }
  }

  const plansSnap = await getDocs(plansCollectionRef(providerId));

  await updateDoc(providerDocRef(providerId), {
    planCount: plansSnap.size,
    updatedAt: new Date().toISOString(),
  });
};

export const updateProviderWithPlans = async ({
  providerId,
  provider,
  providerLogoFile = null,
  plans = [],
  performedBy = "",
}) => {
  const providerName = provider.providerName?.trim();

  if (!providerName) {
    throw new Error("Provider name is required.");
  }


  const providerRef = providerDocRef(providerId);
  const previousProvider = await getProviderById(providerId);

  let logoData = {
    providerLogoUrl: provider.providerLogoUrl || "",
    providerLogoPath: provider.providerLogoPath || "",
  };

  if (providerLogoFile) {
    logoData = await uploadProviderLogo({
      providerId,
      file: providerLogoFile,
    });
  }

  const validPlans = plans.filter((plan) => plan.planName?.trim());

  await updateDoc(providerRef, {
    providerName,
    providerNameLower: providerName.toLowerCase(),
    providerLogoUrl: logoData.providerLogoUrl,
    providerLogoPath: logoData.providerLogoPath,
    planCount: validPlans.length,
    updatedAt: new Date().toISOString(),
  });
  for (const plan of validPlans) {
    const planName = plan.planName.trim();

    const currentPlanRef = plan.id
      ? planDocRef(providerId, plan.id)
      : doc(plansCollectionRef(providerId));

    const newAttachments = plan.attachmentFiles?.length
      ? await uploadPlanAttachments({
          providerId,
          planId: currentPlanRef.id,
          files: plan.attachmentFiles,
        })
      : [];

    const existingAttachments = plan.attachments || [];

    await setDoc(
      currentPlanRef,
      {
        planName,
        planNameLower: planName.toLowerCase(),
        planType: plan.planType || "",
        policyStartingWith: plan.policyStartingWith || "",

        addresses: (plan.addresses || []).filter(Boolean),
        emails: (plan.emails || []).filter(Boolean),
        contacts: (plan.contacts || []).filter((contact) =>
          contact?.number?.trim(),
        ),
        websites: (plan.websites || []).filter(Boolean),

        loaGuidelines: {
          acceptsScannedDocs: plan.loaGuidelines?.acceptsScannedDocs ?? null,
          acceptsLoaByPostOnly:
            plan.loaGuidelines?.acceptsLoaByPostOnly ?? null,
          acceptsDigitallySignedDocs:
            plan.loaGuidelines?.acceptsDigitallySignedDocs ?? null,
          acceptsWetSignatureOnly:
            plan.loaGuidelines?.acceptsWetSignatureOnly ?? null,
          acceptsLoaInsteadOfAuthority:
            plan.loaGuidelines?.acceptsLoaInsteadOfAuthority ?? null,
          secureEmail: plan.loaGuidelines?.secureEmail ?? null,
        },

        notes: plan.notes || "",
        attachments: [...existingAttachments, ...newAttachments],
        updatedAt: new Date().toISOString(),
        createdAt: plan.createdAt || new Date().toISOString(),
      },
      { merge: true },
    );
  }

const changes = buildProviderUpdateChanges({
  previousProvider,
  providerName,
  logoData,
  validPlans,
});

if (changes.length) {
  await createProviderNetworkLog({
    providerId,
    providerName,
    action: "updated",
    performedBy,
    summary: `Updated provider ${providerName}`,
    changes,
  });
}

  return { providerId };
};